/**
 * The one place ui-kit turns an unknown thrown value into operator-visible text.
 *
 * Background (P1-8.5): `ErrorState` used to narrow with
 * `message instanceof Error ? message.message : typeof message === 'string' ?
 * message : 'An unknown error occurred'`. `@tyndo/hooks` threw a PLAIN OBJECT,
 * so the first branch was false and every governed refusal — an SoD denial, an
 * ISA-18.2 shelving refusal, the RFC-7807 `detail` the gateway goes to trouble
 * to preserve — was rendered to the operator as "An unknown error occurred".
 * The reason was extracted correctly and then thrown away at the last step.
 *
 * The primary fix is at the type boundary: the hooks now throw a real `Error`
 * subclass (`ApiRequestError`), so the `instanceof` branch is true. This module
 * is the second half — it also reads the shapes that never pass through the
 * hook: a raw ProblemDetails body, the legacy `{ error: "…" }` module
 * envelope, a fetch `Response`-ish `{ statusText }`, or a module test fixture.
 *
 * Deliberately dependency-free: ui-kit does NOT depend on `@tyndo/hooks` and
 * must not start to. The precedence below mirrors `extractErrorMessage` there
 * (`detail → title → message → error → statusText`), so the two agree on every
 * shape the gateway actually returns.
 */

/** Read a non-empty string property off a plain object, else undefined. */
function text(source: Record<string, unknown>, key: string): string | undefined {
  const value = source[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * Best-effort operator-readable message for an unknown thrown value.
 *
 * @param value    the thrown value: `Error`, `ApiRequestError`, ProblemDetails
 *                 body, legacy envelope, string, or anything else.
 * @param fallback the copy to use when the value carries no usable reason.
 *                 Callers pass their own so the fallback can be specific.
 */
export function resolveErrorMessage(value: unknown, fallback: string): string {
  if (typeof value === 'string') return value.length > 0 ? value : fallback;
  if (value instanceof Error) return value.message.length > 0 ? value.message : fallback;
  if (value && typeof value === 'object') {
    const source = value as Record<string, unknown>;
    return (
      text(source, 'detail') ??
      text(source, 'title') ??
      text(source, 'message') ??
      text(source, 'error') ??
      text(source, 'statusText') ??
      fallback
    );
  }
  return fallback;
}
